import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Checkbox,
  Dropdown,
  Empty,
  Input,
  Modal,
  Pagination,
  Select,
  Space,
  Spin,
  Tooltip,
  message,
} from 'antd'
import {
  AppstoreOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlayCircleFilled,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SendOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import {
  batchDeleteVideoAssets,
  createPublishItem,
  deleteVideoAsset,
  getVideoAssetStats,
  listVideoAssets,
  videoAssetPlaybackUrl,
  videoAssetPosterUrl,
  type VideoAssetDurationFilter,
  type VideoAssetItem,
  type VideoAssetResolutionFilter,
  type VideoAssetSourceFilter,
  type VideoAssetStats,
  type VideoAssetTeamBlock,
  type VideoLibraryView,
} from '../api'
import { useWorkspaceState } from './WorkspaceContext'
import { VistoeraActionConfirm } from '../ui/action'
import {
  buildCreatorSelectOptions,
  creatorFilterAfterViewChange,
  sanitizeCreatorFilter,
} from './team/creatorFilterOptions'
import VideoUploadModal from './VideoUploadModal'
import VideoAssetPreviewModal from './VideoAssetPreviewModal'
import {
  videoAssetOriginLabel,
  videoAssetOriginNavigable,
  videoAssetOriginSecondary,
} from './videoAssetOrigin'
import './video-asset-center.css'

const PAGE_SIZE_OPTIONS = [20, 50, 100]
const VIEW_MODE_KEY = 'video_asset_view_mode'

type SourceCardKey = 'all' | 'ai_generated' | 'upload' | 'published'
type SummaryCardKey = SourceCardKey | 'total_duration'

const SOURCE_BADGE_CLASS: Record<VideoAssetItem['source_kind'], string> = {
  ai_generated: 'vac-badge-ai_generated',
  continuous_generated: 'vac-badge-continuous_generated',
  upload: 'vac-badge-upload',
}

function sourceBadgeClass(item: VideoAssetItem): string {
  return `vac-badge ${SOURCE_BADGE_CLASS[item.source_kind] || ''}`
}

function formatDuration(seconds: number | null): string {
  // VISTOERA_VIDEO_ASSETS_V1 / TASK-00120 contract (see
  // `videoIngestion.contract.test.mjs`) — an unknown or zero/negative
  // duration intentionally renders the dash placeholder rather than
  // "0:00". Left as-is; VistoeraTASK-00133 does not change this contract.
  if (seconds == null || Number.isNaN(seconds) || seconds <= 0) return '—'
  const total = Math.round(seconds)
  if (total <= 0) return '—'
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function formatBytes(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function formatResolution(w: number | null, h: number | null): string {
  if (!w || !h) return '—'
  const longSide = Math.max(w, h)
  if (longSide >= 3200) return '4K'
  if (longSide >= 1800) return '1080P'
  if (longSide >= 1200) return '720P'
  return `${w}×${h}`
}

function formatAbsoluteTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${y}-${mo}-${day} ${hh}:${mm}`
}

function readStoredViewMode(): 'grid' | 'list' {
  try {
    const v = localStorage.getItem(VIEW_MODE_KEY)
    if (v === 'list' || v === 'grid') return v
  } catch {
    /* ignore */
  }
  return 'grid'
}

export default function VideoAssetsPage() {
  const navigate = useNavigate()
  const { t } = useTranslation('video')
  const { t: tImageTeam } = useTranslation('imageAssets')
  const { t: tActions } = useTranslation('actions')
  const { workspace, entitlements } = useWorkspaceState()

  const sourceLabel = useCallback(
    (item: VideoAssetItem): string => {
      const kind = item.source_kind
      if (kind === 'ai_generated' || kind === 'continuous_generated' || kind === 'upload') {
        return t(`assets.sourceKinds.${kind}`)
      }
      return item.source_kind
    },
    [t],
  )

  const originLabel = useCallback((item: VideoAssetItem): string => videoAssetOriginLabel(item, t), [t])

  const onOriginClick = useCallback(
    (item: VideoAssetItem) => {
      if (!videoAssetOriginNavigable(item) || !item.origin_navigate_route) return
      navigate(item.origin_navigate_route)
      if (item.origin_deleted_hint) {
        message.info(item.origin_deleted_hint)
      }
    },
    [navigate],
  )

  const renderOriginCell = (item: VideoAssetItem) => {
    const label = originLabel(item)
    const secondary = videoAssetOriginSecondary(item)
    const navigable = videoAssetOriginNavigable(item)
    const tooltip = item.origin_deleted_hint || secondary || undefined
    const content = navigable ? (
      <button type="button" className="vac-origin-link" onClick={() => onOriginClick(item)}>
        {label}
      </button>
    ) : (
      <span className="vac-origin-static">{label}</span>
    )
    return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content
  }
  const workspaceId = workspace?.id || ''
  const videoGenEnabled = Boolean(entitlements['feature.video_generation'])

  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<VideoAssetItem[]>([])
  const [total, setTotal] = useState(0)
  const [stats, setStats] = useState<VideoAssetStats | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [q, setQ] = useState('')
  const [sourceCard, setSourceCard] = useState<SourceCardKey>('all')
  const [sourceFilter, setSourceFilter] = useState<VideoAssetSourceFilter | 'all'>('all')
  const [durationFilter, setDurationFilter] = useState<VideoAssetDurationFilter | 'all'>('all')
  const [resolutionFilter, setResolutionFilter] = useState<VideoAssetResolutionFilter | 'all'>('all')
  const [sort, setSort] = useState<'recent' | 'oldest'>('recent')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(readStoredViewMode)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const [uploadOpen, setUploadOpen] = useState(false)
  const [previewItem, setPreviewItem] = useState<VideoAssetItem | null>(null)

  const [team, setTeam] = useState<VideoAssetTeamBlock | null>(null)
  const teamEnabled = Boolean(team?.enabled)
  const teamEnabledRef = useRef(false)
  const [libraryView, setLibraryView] = useState<VideoLibraryView>('mine')
  // Team Workspaces only — mirrors Image Asset Center creator filter (created_by_user_id).
  const [creatorFilter, setCreatorFilter] = useState<string>('all')

  // Keep an already-open preview modal in sync with the list it was opened
  // from — without this, saving an edit (or any other refreshAll()-driven
  // refetch) would leave the modal showing its stale pre-edit snapshot
  // until the user closed and reopened it.
  useEffect(() => {
    if (!previewItem) return
    const fresh = items.find((i) => i.id === previewItem.id)
    if (fresh && fresh !== previewItem) setPreviewItem(fresh)
  }, [items, previewItem])

  const persistViewMode = (mode: 'grid' | 'list') => {
    setViewMode(mode)
    try {
      localStorage.setItem(VIEW_MODE_KEY, mode)
    } catch {
      /* ignore */
    }
  }

  const load = useCallback(async () => {
    if (!workspaceId) return
    setLoading(true)
    setLoadError(null)
    try {
      const { data } = await listVideoAssets(workspaceId, {
        search: q.trim() || undefined,
        source: sourceFilter === 'all' ? undefined : sourceFilter,
        duration: durationFilter === 'all' ? undefined : durationFilter,
        resolution: resolutionFilter === 'all' ? undefined : resolutionFilter,
        sort,
        page,
        page_size: pageSize,
        ...(teamEnabledRef.current
          ? {
              view: libraryView,
              ...(creatorFilter !== 'all' ? { creator_id: creatorFilter } : {}),
            }
          : {}),
      })
      teamEnabledRef.current = Boolean(data.team?.enabled)
      setTeam(data.team ?? null)
      setItems(data.items)
      setTotal(data.total)
      setCreatorFilter((prev) => sanitizeCreatorFilter(prev, data.team?.creator_options))
      setSelectedIds((prev) => {
        if (prev.size === 0) return prev
        const keep = new Set(data.items.map((i) => i.id))
        const next = new Set([...prev].filter((id) => keep.has(id)))
        return next.size === prev.size ? prev : next
      })
    } catch (e) {
      setLoadError((e as Error).message || t('assets.toast.loadFailed'))
      message.error((e as Error).message || t('assets.toast.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [
    workspaceId,
    q,
    sourceFilter,
    durationFilter,
    resolutionFilter,
    sort,
    page,
    pageSize,
    libraryView,
    creatorFilter,
    t,
  ])

  const loadStats = useCallback(async () => {
    if (!workspaceId) return
    try {
      const { data } = await getVideoAssetStats(
        workspaceId,
        teamEnabledRef.current ? { view: libraryView } : undefined,
      )
      if (data.team) {
        teamEnabledRef.current = Boolean(data.team.enabled)
        setTeam(data.team)
      }
      setStats(data)
    } catch {
      /* Stats are supplementary — a failure here must never blank the grid. */
    }
  }, [workspaceId, libraryView])

  const onChangeLibraryView = (next: VideoLibraryView) => {
    if (next === libraryView) return
    setLibraryView(next)
    setCreatorFilter((prev) => creatorFilterAfterViewChange(next, prev))
    setPage(1)
  }

  useEffect(() => {
    setLibraryView('mine')
    setTeam(null)
    setCreatorFilter('all')
    teamEnabledRef.current = false
  }, [workspaceId])

  const creatorLabel = (item: VideoAssetItem): string => {
    if (!item.creator) return tImageTeam('team.creator.unknown')
    return item.creator.is_me ? tImageTeam('team.creator.me') : item.creator.display_name
  }

  const creatorInitial = (name: string | null | undefined): string => {
    const s = (name || '').trim()
    return s ? s.charAt(0).toUpperCase() : '?'
  }

  const avatarColor = (seed: string | null | undefined): string | undefined => {
    if (!seed) return undefined
    let h = 0
    for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0
    const hue = h % 360
    return `hsl(${hue} 42% 42%)`
  }

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    void loadStats()
  }, [loadStats])

  const refreshAll = () => {
    void load()
    void loadStats()
  }

  const deleteAsset = async (item: VideoAssetItem) => {
    try {
      await deleteVideoAsset(workspaceId, item.id)
      message.success(t('assets.toast.deleted'))
      if (previewItem && previewItem.id === item.id) setPreviewItem(null)
      setSelectedIds((prev) => {
        const next = new Set(prev)
        next.delete(item.id)
        return next
      })
      refreshAll()
    } catch (e) {
      const msg = (e as { response?: { data?: { detail?: { message?: string } } } })?.response?.data?.detail?.message
      message.error(msg || (e as Error).message || t('assets.toast.deleteFailed'))
      throw e
    }
  }

  const toggleSelect = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const allPageSelected = items.length > 0 && items.every((i) => selectedIds.has(i.id))
  const somePageSelected = items.some((i) => selectedIds.has(i.id))

  const toggleSelectAllPage = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      for (const item of items) {
        if (checked) next.add(item.id)
        else next.delete(item.id)
      }
      return next
    })
  }

  const selectedItems = useMemo(() => items.filter((i) => selectedIds.has(i.id)), [items, selectedIds])

  const onBatchDelete = () => {
    if (selectedItems.length === 0) return
    const ids = selectedItems.map((i) => i.id)
    Modal.confirm({
      title: t('assets.deleteConfirm.batchTitle', { count: ids.length }),
      width: 520,
      centered: true,
      okText: tActions('delete'),
      okType: 'danger',
      cancelText: tActions('cancel'),
      content: (
        <div>
          <p>{t('assets.deleteConfirm.batchIntro')}</p>
          <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
            <li>{t('assets.deleteConfirm.batchBullet1')}</li>
            <li>{t('assets.deleteConfirm.batchBullet2')}</li>
          </ul>
        </div>
      ),
      onOk: async () => {
        try {
          const { data } = await batchDeleteVideoAssets(workspaceId, ids)
          setSelectedIds(new Set())
          refreshAll()
          if (data.failed_count === 0) {
            message.success(t('assets.toast.batchDeleteSuccess', { count: data.deleted_count }))
          } else if (data.deleted_count > 0) {
            message.warning(
              t('assets.toast.batchDeletePartial', {
                deleted: data.deleted_count,
                failed: data.failed_count,
              }),
            )
          } else {
            message.error(t('assets.toast.batchDeleteAllProtected'))
          }
        } catch (e) {
          message.error((e as Error).message || t('assets.toast.batchDeleteFailed'))
        }
      },
    })
  }

  const onUsePublish = async (item: VideoAssetItem) => {
    if (!item.can_publish || !item.job_id) return
    try {
      const { data } = await createPublishItem(workspaceId, item.job_id)
      message.success(t('assets.toast.publishSuccess'))
      navigate(`/workspace/${workspaceId}/content-publishing/${data.id}`)
    } catch (e) {
      message.error((e as Error).message || t('assets.toast.publishFailed'))
    }
  }

  const onSourceCardClick = (key: SourceCardKey) => {
    setSourceCard(key)
    setPage(1)
    setSourceFilter(key === 'all' ? 'all' : key)
  }

  const summaryCards = useMemo<
    Array<{
      key: SummaryCardKey
      label: string
      count: number | string
      hint: string
      clickable: boolean
    }>
  >(
    () => [
      {
        key: 'all',
        label: t('assets.summary.all.label'),
        count: stats?.total ?? '—',
        hint: t('assets.summary.all.hint'),
        clickable: true,
      },
      {
        key: 'ai_generated',
        label: t('assets.summary.ai_generated.label'),
        count: stats?.ai_generated ?? '—',
        hint: t('assets.summary.ai_generated.hint'),
        clickable: true,
      },
      {
        key: 'upload',
        label: t('assets.summary.upload.label'),
        count: stats?.upload ?? '—',
        hint: t('assets.summary.upload.hint'),
        clickable: true,
      },
      {
        key: 'published',
        label: t('assets.summary.published.label'),
        count: stats?.published ?? '—',
        hint: t('assets.summary.published.hint'),
        clickable: true,
      },
      {
        key: 'total_duration',
        label: t('assets.summary.totalDuration.label'),
        count: stats ? t('assets.summary.totalDuration.minutes', { minutes: stats.total_duration_minutes }) : '—',
        hint: t('assets.summary.totalDuration.hint'),
        clickable: false,
      },
    ],
    [stats, t],
  )

  const rowActions = (item: VideoAssetItem) => (
    <div className="vac-row-actions">
      <Tooltip title={tActions('download')}>
        <Button
          type="text"
          size="small"
          className="vac-icon-btn"
          icon={<DownloadOutlined />}
          onClick={() => window.open(videoAssetPlaybackUrl(workspaceId, item, true), '_blank', 'noopener')}
          aria-label={tActions('download')}
        />
      </Tooltip>
      <Tooltip
        title={item.can_publish ? t('assets.actions.useForPublish') : t('assets.actions.publishDisabled')}
      >
        <Button
          type="text"
          size="small"
          className="vac-icon-btn"
          icon={<SendOutlined />}
          disabled={!item.can_publish}
          onClick={() => void onUsePublish(item)}
          aria-label={t('assets.actions.publishAria')}
        />
      </Tooltip>
      <VistoeraActionConfirm
        title={t('assets.deleteConfirm.singleTitle')}
        description={t('assets.deleteConfirm.singleDescription')}
        confirmLabel={tActions('delete')}
        tooltip={tActions('delete')}
        onConfirm={() => deleteAsset(item)}
      >
        <Button
          type="text"
          size="small"
          className="vac-icon-btn vac-icon-danger"
          danger
          icon={<DeleteOutlined />}
          aria-label={tActions('delete')}
        />
      </VistoeraActionConfirm>
    </div>
  )

  if (!workspaceId) return null

  const selectedCount = selectedIds.size
  const hasAnyFilterActive =
    q.trim() !== '' ||
    sourceFilter !== 'all' ||
    durationFilter !== 'all' ||
    resolutionFilter !== 'all' ||
    (teamEnabled && creatorFilter !== 'all')

  return (
    <div className="vac-page" data-testid="video-asset-center">
      <div className="vac-main">
        <header className="vac-header">
          <div className="vac-heading">
            <h1 className="vac-title">{t('assets.title')}</h1>
            <p className="vac-subtitle">{t('assets.subtitle')}</p>
          </div>
          <div className="vac-actions">
            <Button
              icon={<CloudUploadOutlined />}
              disabled={!videoGenEnabled}
              onClick={() => setUploadOpen(true)}
              data-testid="vac-upload-btn"
            >
              {t('assets.uploadButton')}
            </Button>
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'batch-del',
                    label: t('assets.batchActions.delete'),
                    danger: true,
                    disabled: selectedCount === 0,
                    onClick: () => onBatchDelete(),
                  },
                ],
              }}
            >
              <Button>{t('assets.batchActions.label')}</Button>
            </Dropdown>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(`/workspace/${workspaceId}/video-generation`)}
              data-testid="vac-generate-btn"
            >
              {t('assets.generateButton')}
            </Button>
          </div>
        </header>

        {teamEnabled ? (
          <div className="iac-view-tabs" role="tablist" aria-label={tImageTeam('team.viewsAria')} data-testid="vac-view-tabs">
            {(['mine', 'shared', 'all'] as const).map((v) => (
              <button
                key={v}
                type="button"
                role="tab"
                aria-selected={libraryView === v}
                className={`iac-view-tab${libraryView === v ? ' is-active' : ''}`}
                onClick={() => onChangeLibraryView(v)}
                data-testid={`vac-view-tab-${v}`}
              >
                {tImageTeam(`team.views.${v}`)}
              </button>
            ))}
          </div>
        ) : null}

        <section className="vac-summary-cards" aria-label={t('assets.summaryAria')} data-testid="vac-summary-cards">
          {summaryCards.map((card) => (
            <button
              key={card.key}
              type="button"
              data-testid={`vac-summary-card-${card.key}`}
              className={`vac-summary-card${!card.clickable ? ' is-static' : ''}${
                card.clickable && sourceCard === card.key ? ' is-active' : ''
              }`}
              onClick={
                card.clickable && card.key !== 'total_duration'
                  ? () => onSourceCardClick(card.key as SourceCardKey)
                  : undefined
              }
              disabled={!card.clickable}
            >
              <div className="vac-summary-label">{card.label}</div>
              <div className="vac-summary-count">
                {typeof card.count === 'number' ? card.count.toLocaleString() : card.count}
              </div>
              <div className="vac-summary-hint">{card.hint}</div>
            </button>
          ))}
        </section>

        <section className="vac-filters">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder={t('assets.filters.searchPlaceholder')}
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setPage(1)
            }}
            className="vac-search"
            data-testid="vac-search"
          />
          <Select
            value={sourceFilter}
            onChange={(v) => {
              setSourceFilter(v)
              setSourceCard(v === 'all' ? 'all' : (v as SourceCardKey))
              setPage(1)
            }}
            options={[
              { value: 'all', label: t('assets.filters.allTypes') },
              { value: 'ai_generated', label: t('assets.sourceKinds.ai_generated') },
              { value: 'continuous_generated', label: t('assets.sourceKinds.continuous_generated') },
              { value: 'upload', label: t('assets.sourceKinds.upload') },
              { value: 'published', label: t('assets.filters.published') },
            ]}
            className="vac-filter-select"
          />
          <Select
            value={durationFilter}
            onChange={(v) => {
              setDurationFilter(v)
              setPage(1)
            }}
            options={[
              { value: 'all', label: t('assets.filters.allDuration') },
              { value: 'short', label: t('assets.filters.short') },
              { value: 'medium', label: t('assets.filters.medium') },
              { value: 'long', label: t('assets.filters.long') },
            ]}
            className="vac-filter-select"
          />
          {teamEnabled && libraryView !== 'mine' ? (
            <Select
              value={creatorFilter}
              onChange={(v) => {
                setCreatorFilter(v)
                setPage(1)
              }}
              aria-label={tImageTeam('team.creator.filterAria')}
              className="vac-filter-select vac-creator-filter"
              data-testid="vac-creator-filter"
              options={buildCreatorSelectOptions(
                tImageTeam('team.creator.label'),
                tImageTeam('team.creator.all'),
                tImageTeam('team.creator.me'),
                team?.creator_options,
                team?.user_id,
              )}
            />
          ) : null}
          <Select
            value={sort}
            onChange={(v) => {
              setSort(v)
              setPage(1)
            }}
            options={[
              { value: 'recent', label: t('assets.filters.sortRecent') },
              { value: 'oldest', label: t('assets.filters.sortOldest') },
            ]}
            className="vac-filter-select"
          />
          <Tooltip title={tActions('refresh')}>
            <Button icon={<ReloadOutlined />} onClick={refreshAll} aria-label={tActions('refresh')} />
          </Tooltip>
          <div className="vac-view-toggle" role="group" aria-label={t('assets.view.toggleAria')}>
            <Button
              type={viewMode === 'grid' ? 'primary' : 'default'}
              icon={<AppstoreOutlined />}
              onClick={() => persistViewMode('grid')}
              aria-label={t('assets.view.gridAria')}
              data-testid="vac-view-grid"
            />
            <Button
              type={viewMode === 'list' ? 'primary' : 'default'}
              icon={<UnorderedListOutlined />}
              onClick={() => persistViewMode('list')}
              aria-label={t('assets.view.listAria')}
              data-testid="vac-view-list"
            />
          </div>
        </section>

        {selectedCount > 0 && (
          <div className="vac-batch-bar" data-testid="vac-batch-bar">
            <span>{t('assets.selection.count', { count: selectedCount })}</span>
            <Space>
              <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onBatchDelete()}>
                {t('assets.batchActions.delete')}
              </Button>
              <Button size="small" type="link" onClick={() => setSelectedIds(new Set())}>
                {t('assets.selection.clear')}
              </Button>
            </Space>
          </div>
        )}

        <Spin spinning={loading}>
          {loadError && !loading ? (
            <div style={{ padding: '48px 0', textAlign: 'center' }}>
              <Empty description={loadError} />
              <Button style={{ marginTop: 12 }} onClick={refreshAll}>
                {tActions('retry')}
              </Button>
            </div>
          ) : !loading && items.length === 0 ? (
            <div style={{ padding: '48px 0', textAlign: 'center' }}>
              <Empty
                description={
                  hasAnyFilterActive ? t('assets.empty.noMatch') : t('assets.empty.noAssets')
                }
              />
              {!hasAnyFilterActive && (
                <div className="vac-empty-actions">
                  <Button icon={<CloudUploadOutlined />} disabled={!videoGenEnabled} onClick={() => setUploadOpen(true)}>
                    {t('assets.uploadButton')}
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => navigate(`/workspace/${workspaceId}/video-generation`)}
                  >
                    {t('assets.generateButton')}
                  </Button>
                </div>
              )}
            </div>
          ) : viewMode === 'grid' ? (
            <div className="vac-grid" data-testid="vac-grid">
              {items.map((item, index) => {
                const selected = selectedIds.has(item.id)
                const selIndex = selected ? [...selectedIds].indexOf(item.id) + 1 : 0
                const poster = videoAssetPosterUrl(workspaceId, item)
                return (
                  <article
                    key={item.id}
                    className={`vac-card${selected ? ' is-selected' : ''}`}
                    data-asset-id={item.id}
                    data-source-kind={item.source_kind}
                  >
                    <div className="vac-card-media">
                      <label className="vac-card-check">
                        <Checkbox checked={selected} onChange={(e) => toggleSelect(item.id, e.target.checked)} />
                        {selected && selIndex > 0 ? <span className="vac-sel-index">{selIndex}</span> : null}
                      </label>
                      <VistoeraActionConfirm
                        title={t('assets.deleteConfirm.singleTitle')}
                        description={t('assets.deleteConfirm.singleDescription')}
                        confirmLabel={tActions('delete')}
                        tooltip={tActions('delete')}
                        onConfirm={() => deleteAsset(item)}
                      >
                        <button type="button" className="vac-card-delete" aria-label={tActions('delete')}>
                          <DeleteOutlined />
                        </button>
                      </VistoeraActionConfirm>
                      <button type="button" className="vac-card-preview" onClick={() => setPreviewItem(item)}>
                        {poster ? (
                          <img src={poster} alt={item.title} loading="lazy" />
                        ) : (
                          <span className="vac-card-empty">{t('assets.empty.noPreview')}</span>
                        )}
                        <span className="vac-card-play">
                          <PlayCircleFilled />
                        </span>
                        <span className="vac-card-duration">{formatDuration(item.duration_seconds)}</span>
                      </button>
                    </div>
                    <div className="vac-card-meta">
                      <div className="vac-card-name" title={item.title}>
                        {item.title}
                      </div>
                      <div className="vac-card-tags">
                        <span className={sourceBadgeClass(item)}>{sourceLabel(item)}</span>
                        <span className="vac-source-chip">{renderOriginCell(item)}</span>
                        {item.is_published && <span className="vac-badge vac-badge-published">{t('assets.badges.published')}</span>}
                      </div>
                      <div className="vac-card-sub">
                        <span>{formatResolution(item.width, item.height)}</span>
                        <span>{formatBytes(item.size_bytes)}</span>
                      </div>
                      {teamEnabled ? (
                        <div className="vac-card-creator" data-testid="vac-card-creator">
                          <span className="vac-creator" title={creatorLabel(item)}>
                            <span
                              className="vac-avatar"
                              aria-hidden
                              style={{ background: avatarColor(item.creator?.user_id) }}
                            >
                              {creatorInitial(item.creator?.display_name)}
                            </span>
                            <span className="vac-creator-name">{creatorLabel(item)}</span>
                          </span>
                        </div>
                      ) : null}
                      <div className="vac-card-footer">
                        <span>{formatAbsoluteTime(item.created_at)}</span>
                        <span aria-hidden style={{ opacity: 0 }}>
                          {index + 1}
                        </span>
                      </div>
                    </div>
                  </article>
                )
              })}
            </div>
          ) : (
            <div className="vac-table-scroll" data-testid="vac-list">
              <table className="vac-table">
                <thead>
                  <tr>
                    <th className="vac-th-check">
                      <Checkbox
                        checked={allPageSelected}
                        indeterminate={!allPageSelected && somePageSelected}
                        onChange={(e) => toggleSelectAllPage(e.target.checked)}
                        aria-label={t('assets.table.selectAllPage')}
                      />
                    </th>
                    <th className="vac-th-thumb">{t('assets.table.thumbnail')}</th>
                    <th className="vac-th-name">{t('assets.table.name')}</th>
                    <th>{t('assets.table.type')}</th>
                    <th>{t('assets.table.source')}</th>
                    {teamEnabled ? <th>{tImageTeam('team.creator.label')}</th> : null}
                    <th>{t('assets.table.duration')}</th>
                    <th>{t('assets.table.resolutionAndSize')}</th>
                    <th>{t('assets.table.createdAt')}</th>
                    <th className="vac-th-ops">{t('assets.table.operations')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const poster = videoAssetPosterUrl(workspaceId, item)
                    return (
                      <tr key={item.id} className={selectedIds.has(item.id) ? 'is-selected' : undefined} data-asset-id={item.id}>
                        <td className="vac-td-check">
                          <Checkbox checked={selectedIds.has(item.id)} onChange={(e) => toggleSelect(item.id, e.target.checked)} />
                        </td>
                        <td className="vac-td-thumb">
                          <button
                            type="button"
                            className="vac-list-thumb"
                            onClick={() => setPreviewItem(item)}
                            aria-label={t('assets.table.previewAria', { title: item.title })}
                          >
                            {poster ? <img src={poster} alt="" loading="lazy" /> : null}
                          </button>
                        </td>
                        <td className="vac-td-name">
                          <div className="vac-list-name" title={item.title}>
                            {item.title}
                          </div>
                          <div className="vac-list-dims">{formatDuration(item.duration_seconds)}</div>
                        </td>
                        <td>
                          <span className={sourceBadgeClass(item)}>{sourceLabel(item)}</span>
                        </td>
                        <td className="vac-td-origin">{renderOriginCell(item)}</td>
                        {teamEnabled ? (
                          <td className="vac-td-creator" data-testid="vac-row-creator">
                            <span className="vac-creator" title={creatorLabel(item)}>
                              <span
                                className="vac-avatar"
                                aria-hidden
                                style={{ background: avatarColor(item.creator?.user_id) }}
                              >
                                {creatorInitial(item.creator?.display_name)}
                              </span>
                              <span className="vac-creator-name">{creatorLabel(item)}</span>
                            </span>
                          </td>
                        ) : null}
                        <td>{formatDuration(item.duration_seconds)}</td>
                        <td className="vac-td-size">
                          <div>{formatResolution(item.width, item.height)}</div>
                          <div className="vac-muted">{formatBytes(item.size_bytes)}</div>
                        </td>
                        <td className="vac-td-time">{formatAbsoluteTime(item.created_at)}</td>
                        <td className="vac-td-ops">{rowActions(item)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Spin>

        <footer className="vac-pagination">
          <span className="vac-total">{t('assets.pagination.total', { count: total })}</span>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showSizeChanger
            pageSizeOptions={PAGE_SIZE_OPTIONS.map(String)}
            onChange={(p, ps) => {
              setPage(p)
              setPageSize(ps)
            }}
          />
        </footer>
      </div>

      <aside className="vac-aside" aria-label={t('assets.aside.panelAria')}>
        <div className="vac-aside-block">
          <h2>{t('assets.aside.filters')}</h2>
          <div className="vac-filter-group">
            <p className="vac-filter-group-title">{t('assets.aside.sourceType')}</p>
            <div className="vac-filter-chip-list">
              {(
                [
                  ['all', t('assets.filters.all')],
                  ['ai_generated', t('assets.sourceKinds.ai_generated')],
                  ['continuous_generated', t('assets.sourceKinds.continuous_generated')],
                  ['upload', t('assets.sourceKinds.upload')],
                  ['published', t('assets.filters.published')],
                ] as Array<[VideoAssetSourceFilter | 'all', string]>
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`vac-filter-chip${sourceFilter === value ? ' is-active' : ''}`}
                  onClick={() => {
                    setSourceFilter(value)
                    setSourceCard(value === 'all' ? 'all' : (value as SourceCardKey))
                    setPage(1)
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="vac-filter-group">
            <p className="vac-filter-group-title">{t('assets.aside.duration')}</p>
            <div className="vac-filter-chip-list">
              {(
                [
                  ['all', t('assets.filters.all')],
                  ['short', t('assets.filters.short')],
                  ['medium', t('assets.filters.medium')],
                  ['long', t('assets.filters.long')],
                ] as Array<[VideoAssetDurationFilter | 'all', string]>
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`vac-filter-chip${durationFilter === value ? ' is-active' : ''}`}
                  onClick={() => {
                    setDurationFilter(value)
                    setPage(1)
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="vac-filter-group">
            <p className="vac-filter-group-title">{t('assets.aside.resolution')}</p>
            <div className="vac-filter-chip-list">
              {(
                [
                  ['all', t('assets.filters.all')],
                  ['4k', '4K'],
                  ['1080p', '1080P'],
                  ['720p', '720P'],
                  ['other', t('assets.filters.resolutionOther')],
                ] as Array<[VideoAssetResolutionFilter | 'all', string]>
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`vac-filter-chip${resolutionFilter === value ? ' is-active' : ''}`}
                  onClick={() => {
                    setResolutionFilter(value)
                    setPage(1)
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="vac-aside-block">
          <h2>{t('assets.aside.quickActions')}</h2>
          <div className="vac-quick-actions">
            <Button block icon={<CloudUploadOutlined />} disabled={!videoGenEnabled} onClick={() => setUploadOpen(true)}>
              {t('assets.uploadButton')}
            </Button>
            <Button
              block
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(`/workspace/${workspaceId}/video-generation`)}
            >
              {t('assets.generateButton')}
            </Button>
            {/* 视频编辑 — no standalone Video Editor capability exists yet
             * in this product (spec §十一: "如果当前没有独立 Video Editor：
             * 不要为了这个按钮新开发编辑器"); disabled + explained rather
             * than hidden, so the gap is visible instead of silent. */}
            <Tooltip title={t('assets.aside.noEditorTooltip')}>
              <Button block icon={<EditOutlined />} disabled>
                {t('assets.aside.videoEdit')}
              </Button>
            </Tooltip>
            <Button block type="link" onClick={() => navigate(`/workspace/${workspaceId}/video-generation/classic`)}>
              {t('assets.aside.viewTasks')}
            </Button>
          </div>
        </div>
        <div className="vac-aside-block">
          <h2>{t('assets.aside.usageGuide')}</h2>
          <p className="vac-aside-note">{t('assets.aside.usageNote')}</p>
        </div>
      </aside>

      <VideoUploadModal
        open={uploadOpen}
        workspaceId={workspaceId}
        onClose={() => setUploadOpen(false)}
        onUploaded={() => {
          setUploadOpen(false)
          refreshAll()
        }}
      />

      <VideoAssetPreviewModal
        item={previewItem}
        workspaceId={workspaceId}
        onClose={() => setPreviewItem(null)}
        onDelete={deleteAsset}
        onUpdated={() => refreshAll()}
        onPublish={onUsePublish}
      />
    </div>
  )
}
